import { useStore } from "@/stores/useStore";
import { DynamicOptions, Employee, Option } from "@/types/v2";
import { DEFAULT_COLORS } from "@/lib/const";
import { useCallback, useMemo } from "react";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import { buildVehicleMakeOptions } from "@/lib/vehicleMakes";

const mapEmployeesToOptions = (employees: Employee[]): Option[] => {
  return employees
    .map(
      (e): Option => ({ label: e.firstName + " " + e.lastName, value: e.id })
    )
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const useDynamicOptions = () => {
  const { shop, employees, tags, cards } = useStore();
  const serviceWriters = useServiceAdvisors();

  const customColors = useMemo(
    () => shop?.cardConfig?.cardColors ?? DEFAULT_COLORS,
    [shop]
  );
  const containers = useMemo(() => shop?.containers ?? [], [shop]);
  const technicians = useMemo(() => employees ?? [], [employees]);
  const shopTags = useMemo(() => tags?.tags ?? [], [tags]);

  const colorOptions = useMemo((): Option[] => {
    return customColors
      .map(
        (c): Option => ({
          label:
            c.title === ""
              ? (DEFAULT_COLORS.find((dc) => dc.id === c.id)?.title ?? "")
              : c.title,
          value: c.color,
        })
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customColors]);

  const containerOptions = useMemo((): Option[] => {
    return containers
      .map((c): Option => ({ label: c.displayName ?? c.id, value: c.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [containers]);

  const technicianOptions = useMemo(
    (): Option[] => mapEmployeesToOptions(technicians),
    [technicians]
  );

  const assigneeOptions = useMemo(
    (): Option[] => mapEmployeesToOptions(employees ?? []),
    [employees]
  );

  const serviceWriterOptions = useMemo(
    (): Option[] => mapEmployeesToOptions(serviceWriters),
    [serviceWriters]
  );

  // Tags are referenced on cards by label, so the option value is the label.
  const tagOptions = useMemo((): Option[] => {
    return shopTags
      .map((t): Option => ({ label: t.label, value: t.label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [shopTags]);

  // Curated manufacturers unioned with whatever is actually on this shop's
  // board, so a make our list misses is still selectable where it matters.
  const vehicleMakeOptions = useMemo(
    (): Option[] => buildVehicleMakeOptions(cards ?? []),
    [cards]
  );

  const generateOptions = useCallback(
    (options: Option[] | undefined, type?: DynamicOptions | null) => {
      if (type === "colors") {
        return colorOptions;
      } else if (type === "containers") {
        return containerOptions;
      } else if (type === "technicians") {
        return technicianOptions;
      } else if (type === "serviceAdvisors") {
        return serviceWriterOptions;
      } else if (type === "tags") {
        return tagOptions;
      } else if (type === "assignees") {
        return assigneeOptions;
      } else if (type === "vehicleMakes") {
        return vehicleMakeOptions;
      }
      return options;
    },
    [
      colorOptions,
      containerOptions,
      technicianOptions,
      serviceWriterOptions,
      tagOptions,
      assigneeOptions,
      vehicleMakeOptions,
    ]
  );

  return { generateOptions };
};
